import React, { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  FormControl,
  MenuItem,
  Select,
  FormControlLabel,
  Radio,
  RadioGroup,
  styled,
} from "@mui/material";
import color from "../../../../components/utils/Colors";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { useNavigate } from "react-router-dom";
import {
  cameraType,
  languages,
  microphoneType,
  operatingSystem,
} from "../../../../components/utils/Data";
import { useDropzone } from "react-dropzone";

interface CreateCourseStepsProps {
  open: boolean;
  handleClose: () => void;
}

const TestVideoSteps: React.FC<CreateCourseStepsProps> = ({
  open,
  handleClose,
}) => {
  const [step, setStep] = useState(0);

  const [formData, setFormData] = useState({
    selectedLanguage: "",
    filmingOptions: "",
    cameraType: "",
    microphoneType: "",
    operatingSystem: "",
  });

  const handleOptionChange = (event: any) => {
    const { name, value } = event.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleRadioOptionChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = event.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  //   const handleInputChange = (e: { target: { name: any; value: any } }) => {
  //     const { name, value } = e.target;
  //     setFormData({
  //       ...formData,
  //       [name]: value,
  //     });
  //   };

  const handleNext = () => {
    if (step < 3) {
      setStep(step + 1);
    } else {
      handleClose();
      console.log("Final Form Data:", formData);
    }
  };

  const handlePrevious = () => {
    if (step === 0) {
      handleClose();
    }
    if (step > 0) {
      setStep(step - 1);
    }
  };

  const isDisabled =
    (step === 0 && formData.selectedLanguage.length === 0) ||
    (step === 1 && formData.filmingOptions.length === 0) ||
    (step === 2 &&
      (formData.cameraType.length === 0 ||
      formData.microphoneType.length === 0 ||
      formData.operatingSystem.length === 0));

  const navigate = useNavigate();

  const handleFinish = () => {
    if (step === 3) {
      navigate("/course-testVideo");
    } else {
      handleNext();
    }
  };

  const CustomCheckbox = styled(Radio)(({ theme }) => ({
    color: color.firstColor,
    "&.Mui-checked": {
      color: color.firstColor,
    },
    "&:hover": {
      backgroundColor: "rgba(76, 175, 80, 0.1)",
    },
    "&.MuiCheckbox-root": {
      borderRadius: "4px",
      //   transform: 'scale(1.2)',
    },
  }));

  const [file, setFile] = useState<File | null>(null);

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      "video/*": [".mp4", ".mov", ".avi", ".mkv"],
    },
    onDrop: (acceptedFiles) => {
      setFile(acceptedFiles[0]);
    },
  });

  const renderModalContent = () => {
    switch (step) {
      case 0:
        return (
          <div
            style={{
              border: "solid 2px",
              borderColor: color.firstColor,
              borderRadius: "60px 2px 60px 2px",
              padding: "40px 30px",
              paddingBottom: "60px",
              marginTop: "20px",
            }}
          >
            <Typography
              sx={{
                textAlign: "center",
                fontWeight: "bold",
                color: color.textColor,
                fontStyle: "italic",
                fontSize: "20px",
              }}
            >
              What language will you speak in your test video?
            </Typography>
            <Typography
              sx={{
                textAlign: "center",
                color: color.textColor,
                fontSize: "12px",
                marginBottom: "10px",
                marginTop: "10px",
              }}
            >
              Select your video language
            </Typography>

            <FormControl className="input-root2" fullWidth>
              <Select
                id="selectedLanguage"
                name="selectedLanguage"
                value={formData.selectedLanguage}
                onChange={handleOptionChange}
                displayEmpty
              >
                <MenuItem value="" disabled>
                  Select Language
                </MenuItem>
                {languages.map((languages) => (
                  <MenuItem key={languages.value} value={languages.value}>
                    {languages.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        );
      case 1:
        return (
          <div
            style={{
              border: "solid 2px",
              borderColor: color.firstColor,
              borderRadius: "60px 2px 60px 2px",
              padding: "40px 30px",
              paddingBottom: "60px",
              marginTop: "20px",
            }}
          >
            <Typography
              sx={{
                textAlign: "center",
                fontWeight: "bold",
                color: color.textColor,
                fontStyle: "italic",
                fontSize: "20px",
              }}
            >
              What are you filming for the test video?
            </Typography>
            <Typography
              sx={{
                textAlign: "center",
                color: color.textColor,
                fontSize: "12px",
                marginBottom: "10px",
                marginTop: "10px",
              }}
            >
              (You can try out different filming methods in one video or test
              each individually, but we can only review one video at a time.)
            </Typography>

            <FormControl component="fieldset">
              <RadioGroup
                name="filmingOptions"
                value={formData.filmingOptions}
                onChange={handleRadioOptionChange}
              >
                <FormControlLabel
                  value="laptopScreen"
                  control={<CustomCheckbox />}
                  label="I'm filming my computer/laptop screen"
                />
                <FormControlLabel
                  value="myself"
                  control={<CustomCheckbox />}
                  label="I'm filming myself or another person"
                />
                <FormControlLabel
                  value="activity"
                  control={<CustomCheckbox />}
                  label="I'm filming an activity (e.g. chemistry practical)"
                />
              </RadioGroup>
            </FormControl>
          </div>
        );
      case 2:
        return (
          <div
            style={{
              border: "solid 2px",
              borderColor: color.firstColor,
              borderRadius: "60px 2px 60px 2px",
              padding: "40px 30px",
              paddingBottom: "60px",
              marginTop: "20px",
            }}
          >
            <Typography
              sx={{
                textAlign: "center",
                fontWeight: "bold",
                color: color.textColor,
                fontStyle: "italic",
                fontSize: "20px",
                marginBottom: "10px",
              }}
            >
              What are you using to create or record video?
            </Typography>

            <Typography
              sx={{
                textAlign: "center",
                color: color.textColor,
                fontSize: "12px",
                marginBottom: "15px",
                marginTop: "0px",
              }}
            >
              (Knowing this helps us troubleshoot issues, but if you aren't
              sure, select "I don't know.")
            </Typography>

            <FormControl className="input-root2" fullWidth>
              {/* <InputLabel htmlFor="name" sx={inputLabelStyle}>
                Camera type
              </InputLabel> */}
              <Select
                id="cameraType"
                name="cameraType"
                value={formData.cameraType}
                onChange={handleOptionChange}
                displayEmpty
              >
                <MenuItem value="" disabled>
                  Camera type
                </MenuItem>
                {cameraType.map((cameraType) => (
                  <MenuItem key={cameraType} value={cameraType}>
                    {cameraType}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl className="input-root2" sx={{ mt: 2 }} fullWidth>
              {/* <InputLabel htmlFor="name" sx={inputLabelStyle}>
              Microphone type
              </InputLabel> */}
              <Select
                id="microphoneType"
                name="microphoneType"
                value={formData.microphoneType}
                onChange={handleOptionChange}
                displayEmpty
              >
                <MenuItem value="" disabled>
                  Microphone type
                </MenuItem>
                {microphoneType.map((microphoneType) => (
                  <MenuItem key={microphoneType} value={microphoneType}>
                    {microphoneType}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl className="input-root2" sx={{ mt: 2 }} fullWidth>
              {/* <InputLabel htmlFor="name" sx={inputLabelStyle}>
              Operating System 
              </InputLabel> */}
              <Select
                id="operatingSystem"
                name="operatingSystem"
                value={formData.operatingSystem}
                onChange={handleOptionChange}
                displayEmpty
              >
                <MenuItem value="" disabled>
                  Operating System
                </MenuItem>
                {operatingSystem.map((operatingSystem) => (
                  <MenuItem key={operatingSystem} value={operatingSystem}>
                    {operatingSystem}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        );
      case 3:
        return (
          <div
            style={{
              border: "solid 2px",
              borderColor: color.firstColor,
              borderRadius: "60px 2px 60px 2px",
              padding: "40px 30px",
              paddingBottom: "60px",
              marginTop: "20px",
            }}
          >
            <Typography
              sx={{
                textAlign: "center",
                fontWeight: "bold",
                color: color.textColor,
                fontStyle: "italic",
                fontSize: "20px",
              }}
            >
              Upload your test video
            </Typography>

            {/* <Box
              sx={{
                color: "white",
                borderRadius: "8px",
                cursor: "pointer",
                width: "80%",
                alignSelf: "center",
                margin: "auto",
                // maxWidth: '700px',
                alignItems: "center",
                display: "flex",
                justifyContent: "center",
                mt: 4,
              }}
              {...getRootProps()}
              border="1.5px dashed"
              borderColor={color.borderColor}
              padding={4}
              // width={400}
              height={150}
            >
              <input {...getInputProps()} />
              <Typography
                textAlign={"center"}
                sx={{
                  p: 2,
                  fontSize: "18px",
                  borderRadius: "8px",
                  color: color.firstColor,
                }}
              >
                Drag 'n' drop some files here or click to select files
              </Typography>
            </Box> */}

            <Box
              border="2px solid"
              borderColor={color.borderColor}
              sx={{
                display: "flex",
                alignItems: "center",
                borderRadius: "14px",
                mt:10,
              }}
            >
              <Typography
                sx={{
                  borderRadius: "8px",
                  pl: 2,
                  mt: 2,
                  mb: 2,
                  color: color.textColor,
                }}
                variant="body1"
              >
                 Selected file: {file ? file.name : "No files selected"}
              </Typography>

              <Button
                {...getRootProps()}
                id="button"
                sx={{ ml: "auto", bottom: 0, right: 20 }}
                style={{
                  background: color.firstColor,
                  color: "white",
                }}
              >
                Upload Video
                <input {...getInputProps()} />
              </Button>
            </Box>

            <Typography
              sx={{
                textAlign: "start",
                color: color.textColor,
                fontSize: "12px",
                marginBottom: "35px",
                marginTop: "10px",
              }}
            >
            Note: All files should be at least 720p and less than 3.0 GB.
            </Typography>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      sx={{
        "& .MuiDialog-paper": {
          width: { xs: "100vw", md: "65vw" },
          margin: 0,
          maxWidth: "100vw",
          height: { xs: "auto", md: "85vh" },
          paddingBottom: { xs: "40px", md: "0px" },
        },
      }}
    >
      <Box
        sx={{
          backgroundImage: "url(/assets/modal_bg.png)",
          backgroundRepeat: "repeat-y",
          backgroundSize: "cover",
          height: "100%",
        }}
      >
        <DialogTitle style={{ display: "flex", alignItems: "center" }}>
          <Typography
            onClick={handlePrevious}
            sx={{
              background: "none",
              color: color.textColor,
              boxShadow: "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mr: "30px",
              cursor: "pointer",
            }}
          >
            <ChevronLeftIcon /> Back
          </Typography>

          <Typography
            sx={{
              color: color.firstColor,
              border: "solid 1px",
              borderColor: color.firstColor,
              width: "fit-content",
              borderRadius: "30px",
              padding: "3px 15px",
              fontSize: "14px",
            }}
          >
            {`Step ${step + 1} of 4`}
          </Typography>
        </DialogTitle>

        <div style={{ background: "#cba4fd", marginBottom: "10px" }}>
          <div
            style={{
              height: "4px",
              background: color.firstColor,
              borderRadius: "0px 10px 10px 0px",
              width:
                step === 0
                  ? "25%"
                  : step === 1
                  ? "50%"
                  : step === 2
                  ? "75%"
                  : "100%",
            }}
          ></div>
        </div>

        <DialogContent>{renderModalContent()}</DialogContent>
        <DialogActions>
          <Button
            id={isDisabled ? "disabled-button" : "button"}
            onClick={handleFinish}
            disabled={isDisabled}
            sx={{ ml: "auto", bottom: 0, right: 20 }}
            style={{
              background: step === 3 ? color.firstColor : "white",
              color: step === 3 ? "white" : isDisabled ?'grey': color.firstColor,
            }}
          >
            {step === 3 ? "Submit For Feedback" : "Next"}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default TestVideoSteps;
